import { ReactElement, useEffect, useMemo, useState } from 'react';

import {
  EMPTY_RATIO_CHART_DATA,
  IRatioChartData,
  useGetRatioChartDataQuery,
} from 'modules/analytics/actions/getRatioChartData';
import { Info } from 'modules/analytics/components/Info/Info';
import { RangeSelector } from 'modules/analytics/components/RangeSelector';
import { RatioChart } from 'modules/analytics/components/RatioChart/RatioChart';
import { RangeSelectorType } from 'modules/api/const';
import { Card, ONE } from 'modules/common';
import { useGetStatsQuery } from 'modules/dashboard/actions/getStats';
import { useGlobalTranslation } from 'modules/i18n/hooks/useGlobalTranslation';

import { translation } from './translation';
import { useStyles } from './useStyles';

export function ExchangeRate(): ReactElement {
  const { t, keys } = useGlobalTranslation(translation);
  const { classes } = useStyles();

  const [range, setRange] = useState(RangeSelectorType.Week);

  const { data } = useGetStatsQuery();

  const rate = data && !data.rate.isZero() ? data.rate : undefined;

  const { data: rawChartData = EMPTY_RATIO_CHART_DATA, isFetching } =
    useGetRatioChartDataQuery(range);

  const [actualRange, setActualRange] = useState<RangeSelectorType>(range);
  const [chartData, setChartData] = useState<IRatioChartData>([]);

  useEffect(() => {
    if (!isFetching && rawChartData.length > 0) {
      setChartData(rawChartData);
      setActualRange(range);
    }
  }, [rawChartData, isFetching, range]);

  const ratedChartData = useMemo(() => {
    return chartData.map(({ ratio, timestamp }) => ({
      ratio: ONE.div(ratio),
      timestamp,
    }));
  }, [chartData]);

  return (
    <Card className={classes.root} wrapClassName={classes.wrapRoot}>
      {range === actualRange && (
        <RatioChart
          className={classes.chart}
          data={ratedChartData}
          range={range}
        />
      )}

      <div className={classes.panel}>
        <Info
          label={t(keys.title)}
          value={
            rate
              ? t(keys.rate, {
                  nativeToken: t(keys.tokens.staked),
                  value: ONE.div(rate).toFixed(4),
                  token: t(keys.tokens.native),
                })
              : t(keys.common.none)
          }
        />

        <RangeSelector
          value={range}
          onChangeValue={newRange => setRange(newRange)}
        />
      </div>
    </Card>
  );
}
